import { useIsAuthenticated } from "@azure/msal-react";
import classNames from "classnames";
import DevBanner from "components/layout/DevBanner/DevBanner";
import ColumnTitle from "components/layout/NavMenu/ColumnTitle/ColumnTitle";
import IrisLoginLink from "components/layout/NavMenu/IrisLoginLink/IrisLoginLink";
import IrisLogoutLink from "components/layout/NavMenu/IrisLoginLink/IrisLogoutLink";
import NavLink from "components/layout/NavMenu/NavLink/NavLink";
import NestedNavLink from "components/layout/NavMenu/NestedNavLink/NestedNavLink";
import {
  ColumnDivider,
  DesktopInsightsLogo,
  DropdownColumn,
  FlexDiv,
  LogoWrapper,
  MenuButton,
  MobileHeader,
  MobileInsightsLogo,
  NavGroupContainer,
  StyledNav,
  DataServicesAndReportsDropdownContainer,
} from "components/layout/NavMenu/style";
import TestBanner from "components/layout/TestBanner/TestBanner";
import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import featureFlags from "utils/featureFlags";
import routeConstants, {
  balancingRoutes,
  elexonBscDataUrl,
  elexonBscPortalUrl,
  dataReportsRoutes,
  demandRoutes,
  elexonBscDataConstants,
  generationRoutes,
  noticesRoutes,
  transmissionRoutes,
} from "utils/routeConstants";

const NavMenu: React.FC = () => {
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [openDropdownKey, setOpenDropdownKey] = useState<string | null>(null);
  const isAuthenticated = useIsAuthenticated();
  const navRef = useRef<HTMLElement | null>(null);

  const closeMenus = (): void => {
    setIsMobileNavOpen(false);
    setOpenDropdownKey(null);
  };

  const toggleDropdown = (key: string): void => {
    setOpenDropdownKey((prev) => (prev === key ? null : key));
  };

  const handleNestedLinkClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ): void => {
    closeMenus();
    event.currentTarget.blur();
  };

  const handleNavMouseDown = (
    event: React.MouseEvent<HTMLElement, MouseEvent>
  ): void => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest("button, a, [role='button'], .navbar-dropdown")) return;

    setOpenDropdownKey(null);
  };

  useEffect(() => {
    const handleDocumentPointerDown = (event: MouseEvent): void => {
      const currentNav = navRef.current;
      if (!currentNav) return;
      if (event.target instanceof Node && !currentNav.contains(event.target)) {
        setOpenDropdownKey(null);
        setIsMobileNavOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentPointerDown);
    return () => {
      document.removeEventListener("mousedown", handleDocumentPointerDown);
    };
  }, []);

  const currentPath = useLocation().pathname;

  return (
    <>
      <StyledNav
        className="navbar"
        ref={navRef}
        data-test-id="navbar"
        onMouseDown={handleNavMouseDown}
      >
        <div className="navbar-brand">
          <MobileHeader>
            <MobileInsightsLogo to="/" onClick={closeMenus}>
              INSIGHTS
            </MobileInsightsLogo>
            {isAuthenticated ? (
              <IrisLogoutLink variant="mobile" />
            ) : (
              <IrisLoginLink variant="mobile" />
            )}
          </MobileHeader>
          <MenuButton
            type="button"
            aria-label="menu"
            aria-expanded={isMobileNavOpen}
            onClick={(): void => setIsMobileNavOpen((e) => !e)}
          >
            Menu
          </MenuButton>
        </div>
        <NavGroupContainer
          className={classNames("navbar-menu", {
            "is-active": isMobileNavOpen,
          })}
        >
          <FlexDiv data-test-id="navbar">
            <LogoWrapper>
              <DesktopInsightsLogo
                to="/"
                data-test-id="nav-link-INSIGHTS"
                onClick={closeMenus}
              >
                INSIGHTS
              </DesktopInsightsLogo>
            </LogoWrapper>
            <ul className="navbar-start">
              <NavLink
                linkText="REMIT"
                linkTo="/remit"
                isActive={currentPath.startsWith("/remit")}
                onClick={closeMenus}
              />
              <NavLink
                linkText="Generation"
                isActive={Object.values(generationRoutes).some((value) =>
                  currentPath.startsWith(value)
                )}
                linkTo={routeConstants.generation}
                dropdownTitleOnClick={closeMenus}
                onClick={(): void => toggleDropdown("Generation")}
                isDropdownOpen={openDropdownKey === "Generation"}
              >
                <ul>
                  <NestedNavLink
                    linkText="Generation by fuel type"
                    linkTo={routeConstants.generationByFuelType}
                    onClick={handleNestedLinkClick}
                  />
                  <NestedNavLink
                    linkText="Interconnector flows"
                    linkTo={routeConstants.interconnectorFlows}
                    onClick={handleNestedLinkClick}
                  />
                  <NestedNavLink
                    linkText="Generation forecast"
                    linkTo={routeConstants.generationForecast}
                    onClick={handleNestedLinkClick}
                  />
                  <NestedNavLink
                    linkText="Generation forecast for wind & solar"
                    linkTo={routeConstants.generationForecastForWindAndSolar}
                    onClick={handleNestedLinkClick}
                  />
                  <NestedNavLink
                    linkText="Wind generation"
                    linkTo={routeConstants.windGeneration}
                    onClick={handleNestedLinkClick}
                  />
                  <NestedNavLink
                    linkText="Actual aggregated generation per type"
                    linkTo={routeConstants.actualGenerationPerType}
                    onClick={handleNestedLinkClick}
                  />
                  <NestedNavLink
                    linkText="Actual or estimated wind and solar power generation"
                    linkTo={
                      routeConstants.actualOrEstimatedWindAndSolarPowerGeneration
                    }
                    onClick={handleNestedLinkClick}
                  />
                  <NestedNavLink
                    linkText="Day-ahead aggregated generation"
                    linkTo={routeConstants.dayAheadAggregatedGeneration}
                    onClick={handleNestedLinkClick}
                  />
                </ul>
              </NavLink>
              <NavLink
                linkText="Demand"
                isActive={Object.values(demandRoutes).some((value) =>
                  currentPath.startsWith(value)
                )}
                linkTo={routeConstants.demand}
                dropdownTitleOnClick={closeMenus}
                onClick={(): void => toggleDropdown("Demand")}
                isDropdownOpen={openDropdownKey === "Demand"}
              >
                <ul>
                  <NestedNavLink
                    linkText="Rolling system demand"
                    linkTo={routeConstants.rollingSystemDemand}
                    onClick={handleNestedLinkClick}
                  />
                  <NestedNavLink
                    linkText="Surplus forecast and margin"
                    linkTo={routeConstants.surplusAndMargin}
                    onClick={handleNestedLinkClick}
                  />
                  <NestedNavLink
                    linkText="Demand outturn"
                    linkTo={routeConstants.demandOutturn}
                    onClick={handleNestedLinkClick}
                  />
                  <NestedNavLink
                    linkText="Daily energy transmitted"
                    linkTo={routeConstants.dailyEnergyTransmitted}
                    onClick={handleNestedLinkClick}
                  />
                  <NestedNavLink
                    linkText="Demand forecast"
                    linkTo={routeConstants.demandForecast}
                    onClick={handleNestedLinkClick}
                  />
                  <NestedNavLink
                    linkText="Indicative peak demand"
                    linkTo={routeConstants.indicativeDemandPeak}
                    onClick={handleNestedLinkClick}
                  />
                  <NestedNavLink
                    linkText="Total load: actual and day-ahead forecast"
                    linkTo={routeConstants.totalLoadActualAndDayAhead}
                    onClick={handleNestedLinkClick}
                  />
                  <NestedNavLink
                    linkText="Total load: week-ahead forecast"
                    linkTo={routeConstants.totalLoadWeekAhead}
                    onClick={handleNestedLinkClick}
                  />
                  <NestedNavLink
                    linkText="Indicated forecasts"
                    linkTo={routeConstants.indicatedForecasts}
                    onClick={handleNestedLinkClick}
                  />
                  <NestedNavLink
                    linkText="Restoration region demand forecast"
                    linkTo={routeConstants.restorationZoneDemandForecast}
                    onClick={handleNestedLinkClick}
                  />
                  <NestedNavLink
                    linkText="Restoration region demand restored"
                    linkTo={routeConstants.restorationZoneDemandRestored}
                    onClick={handleNestedLinkClick}
                  />
                </ul>
              </NavLink>
              <NavLink
                linkText="Balancing"
                isActive={Object.values(balancingRoutes).some((value) =>
                  currentPath.startsWith(value)
                )}
                linkTo={routeConstants.balancing}
                dropdownTitleOnClick={closeMenus}
                onClick={(): void => toggleDropdown("Balancing")}
                isDropdownOpen={openDropdownKey === "Balancing"}
              >
                <ul>
                  <NestedNavLink
                    linkText="Short-term operating reserves"
                    linkTo={routeConstants.shortTermOperatingReserves}
                    onClick={handleNestedLinkClick}
                  />
                  <NestedNavLink
                    linkText="Balancing Mechanism data by BMU view"
                    linkTo={routeConstants.bmuView}
                    onClick={handleNestedLinkClick}
                  />
                  <NestedNavLink
                    linkText="Balancing Mechanism data by market view"
                    linkTo={routeConstants.marketView}
                    onClick={handleNestedLinkClick}
                  />
                  <NestedNavLink
                    linkText="Adjustment data (NETBSAD)"
                    linkTo={routeConstants.adjustmentDataNetbsad}
                    onClick={handleNestedLinkClick}
                  />
                  <NestedNavLink
                    linkText="Adjustment actions (DISBSAD)"
                    linkTo={routeConstants.adjustmentActionsDisbsad}
                    onClick={handleNestedLinkClick}
                  />
                  <NestedNavLink
                    linkText="Market index prices"
                    linkTo={routeConstants.marketIndexPrices}
                    onClick={handleNestedLinkClick}
                  />
                  <NestedNavLink
                    linkText="Derived data"
                    linkTo={routeConstants.derivedData}
                    onClick={handleNestedLinkClick}
                  />
                  <NestedNavLink
                    linkText="Detailed system prices"
                    linkTo={routeConstants.detailedSystemPrices}
                    onClick={handleNestedLinkClick}
                  />
                  <NestedNavLink
                    linkText="System sell and buy prices"
                    linkTo={routeConstants.systemPrices}
                    onClick={handleNestedLinkClick}
                  />
                </ul>
              </NavLink>
              <NavLink
                linkText="Notices"
                isActive={Object.values(noticesRoutes).some((value) =>
                  currentPath.startsWith(value)
                )}
                linkTo={routeConstants.notices}
                dropdownTitleOnClick={closeMenus}
                onClick={(): void => toggleDropdown("Notices")}
                isDropdownOpen={openDropdownKey === "Notices"}
              >
                <ul>
                  <NestedNavLink
                    linkText="Credit default notices"
                    linkTo={routeConstants.creditDefaultNotices}
                    onClick={handleNestedLinkClick}
                  />
                  <NestedNavLink
                    linkText="Events"
                    linkTo={routeConstants.events}
                    onClick={handleNestedLinkClick}
                  />
                  <NestedNavLink
                    linkText="Settlement messages"
                    linkTo={routeConstants.settlementMessages}
                    onClick={handleNestedLinkClick}
                  />
                  <NestedNavLink
                    linkText="SO-SO trade prices"
                    linkTo={routeConstants.sosoTradePrices}
                    onClick={handleNestedLinkClick}
                  />
                </ul>
              </NavLink>
              <NavLink
                linkText="Transmission"
                isActive={Object.values(transmissionRoutes).some((value) =>
                  currentPath.startsWith(value)
                )}
                linkTo={routeConstants.transmission}
                dropdownTitleOnClick={closeMenus}
                onClick={(): void => toggleDropdown("Transmission")}
                isDropdownOpen={openDropdownKey === "Transmission"}
              >
                <ul>
                  <NestedNavLink
                    linkText="Rolling system frequency"
                    linkTo={routeConstants.rollingSystemFrequency}
                    onClick={handleNestedLinkClick}
                  />
                  <NestedNavLink
                    linkText="Temperature"
                    linkTo={routeConstants.temperature}
                    onClick={handleNestedLinkClick}
                  />
                  <NestedNavLink
                    linkText="Loss of load probability (LoLP) and de-rated margin"
                    linkTo={routeConstants.lolpdrm}
                    onClick={handleNestedLinkClick}
                  />
                </ul>
              </NavLink>
              <NavLink
                linkText="Data Services & Reports"
                isActive={Object.values(dataReportsRoutes).some((value) =>
                  currentPath.startsWith(value)
                )}
                linkTo={routeConstants.dataServices}
                dropdownTitleOnClick={closeMenus}
                onClick={(): void => toggleDropdown("Data Services & Reports")}
                isDropdownOpen={openDropdownKey === "Data Services & Reports"}
                hideDropdownTitle={true}
              >
                <DataServicesAndReportsDropdownContainer>
                  <DropdownColumn>
                    <ColumnTitle
                      linkText="DATA SERVICES"
                      linkTo={routeConstants.dataServices}
                      onClick={handleNestedLinkClick}
                    />
                    <ul>
                      <NestedNavLink
                        linkText="Data API endpoints"
                        linkTo={routeConstants.apiDocs}
                        onClick={handleNestedLinkClick}
                      />
                    </ul>
                    <ul>
                      <NestedNavLink
                        linkText="Insights Real-Time Information Service"
                        linkTo={routeConstants.iris}
                        onClick={handleNestedLinkClick}
                      />
                    </ul>
                    <ul>
                      <NestedNavLink
                        linkText="BSC Portal"
                        linkTo={elexonBscPortalUrl}
                        onClick={handleNestedLinkClick}
                        externalLink={true}
                      />
                    </ul>
                  </DropdownColumn>

                  <ColumnDivider />

                  <DropdownColumn>
                    <ColumnTitle
                      linkText="BSC DATA REPORTS"
                      linkTo={elexonBscDataUrl}
                      onClick={handleNestedLinkClick}
                      externalLink={true}
                    />
                    <ul>
                      <NestedNavLink
                        linkText="Trading Operations Report"
                        linkTo={elexonBscDataConstants.tradingOperationsReport}
                        onClick={handleNestedLinkClick}
                        externalLink={true}
                      />
                    </ul>
                    <ul>
                      <NestedNavLink
                        linkText="System Prices Analysis Report"
                        linkTo={
                          elexonBscDataConstants.systemPricesAnalysisReport
                        }
                        onClick={handleNestedLinkClick}
                        externalLink={true}
                      />
                    </ul>
                    <ul>
                      <NestedNavLink
                        linkText="Peer Comparison Graphs"
                        linkTo={elexonBscDataConstants.peerComparisonGraphs}
                        onClick={handleNestedLinkClick}
                        externalLink={true}
                      />
                    </ul>
                    <ul>
                      <NestedNavLink
                        linkText="Gross Supplier Market Share Data reports"
                        linkTo={
                          elexonBscDataConstants.grossSupplierMarketShareDataReports
                        }
                        onClick={handleNestedLinkClick}
                        externalLink={true}
                      />
                    </ul>
                    <ul>
                      <NestedNavLink
                        linkText="Industry Standing Data"
                        linkTo={elexonBscDataConstants.industryStandingData}
                        onClick={handleNestedLinkClick}
                        externalLink={true}
                      />
                    </ul>
                    <ul>
                      <NestedNavLink
                        linkText="Market Domain Data"
                        linkTo={elexonBscDataConstants.marketDomainData}
                        onClick={handleNestedLinkClick}
                        externalLink={true}
                      />
                    </ul>
                    <ul>
                      <NestedNavLink
                        linkText="Open Settlement Data"
                        linkTo={elexonBscDataConstants.openSettlementData}
                        onClick={handleNestedLinkClick}
                        externalLink={true}
                      />
                    </ul>
                    <ul>
                      <NestedNavLink
                        linkText="Data flows"
                        linkTo={elexonBscDataConstants.dataFlows}
                        onClick={handleNestedLinkClick}
                        externalLink={true}
                      />
                    </ul>
                    <ul>
                      <NestedNavLink
                        linkText="Open Data Requests"
                        linkTo={elexonBscDataConstants.openDataRequests}
                        onClick={handleNestedLinkClick}
                        externalLink={true}
                      />
                    </ul>
                    <ul>
                      <NestedNavLink
                        linkText="Short Term Operating Reserve (STOR) Availability Window"
                        linkTo={
                          elexonBscDataConstants.shortTermOperatingReserveAvailabilityWindow
                        }
                        onClick={handleNestedLinkClick}
                        externalLink={true}
                      />
                    </ul>
                  </DropdownColumn>
                </DataServicesAndReportsDropdownContainer>
              </NavLink>
            </ul>
            {isAuthenticated ? (
              <IrisLogoutLink variant="desktop" />
            ) : (
              <IrisLoginLink variant="desktop" />
            )}
          </FlexDiv>
        </NavGroupContainer>
      </StyledNav>
      {featureFlags.displayTestBanner && <TestBanner />}
      {featureFlags.displayDevBanner && <DevBanner />}
    </>
  );
};

export default NavMenu;
